import { tz } from 'moment-timezone';
import { SelectOptionElement } from '@odin-labs/components';
import {
  OnboardingStepSkipReason,
  StateAbbreviation,
} from 'apollo/generated/client-operations';
import { titleToSnakeCase } from 'utils';

export type FancySelectOption = {
  value: string;
  label: string;
  key?: string;
};

export const toFancySelectOptions = (
  options: Array<string>,
): Array<FancySelectOption> =>
  options.map((option) => ({
    value: option,
    label: option,
    key: titleToSnakeCase(option),
  }));

export const veteranOptions = ['Veteran', 'Non veteran'];

export const raceOptions = [
  'White',
  'Black/African American',
  'Hispanic/Latino',
  'Asian/Native Hawaiian or Pacific Islander',
  'Native American/Alaskan Native',
  'Other/Prefer not to respond',
];

export const genderOptions = ['Male', 'Female', 'Other'];

export const primaryLanguageOptions = [
  'English',
  'Spanish',
  'Chinese',
  'Russian',
  'Hungarian',
  'Polish',
  'Turkish',
  'French',
  'Other',
];

export const emergencyContactRelationshipOptions = [
  'Boyfriend',
  'Child',
  'Co-worker',
  'Family',
  'Girlfriend',
  'Grandparent',
  'Friend',
  'Parent',
  'Partner',
  'Spouse',
  'Other',
];

export const DEFAULT_ACTIVITY_CODE_OPTIONS = [
  'Hanging/Trim',
  'Mechanic - Other',
  'Finishing',
  'Labor/Clean Up',
  'Foreman',
  'Framing/Blocking',
  'Ceilings',
  'Doors/Frames/Hardware',
];

// this array should be kept in sync with the options defined in the API
// in `api/src/tasks/workersImport.ts`
export const tradeExperienceOptions = [
  '0 - 1 years',
  '1 - 3 years',
  '3 - 5 years',
  '5 - 10 years',
  '10+ years',
];

export const oshaCardTypes = [
  'OSHA 10 hour construction',
  'OSHA 30 hour construction',
  'OSHA construction trainer card',
  'Disaster Site Trainer card',
  'Maritime Standards card',
];

export const sstCardTypes = [
  'Temporary',
  'Limited Site',
  'Site Safety',
  'Site Safety Training Supervisor',
];

export const govCardTypes = ['Driver license', 'Other government ID'];

export const otherCardTypes = [
  'Passport',
  'Birth Certificate',
  'Social Security Card',
  'Certificate of Naturalization',
  'Certificate of Citizenship',
  'COVID-19 survey',
  'CPR/First Aid, FDNY - F01 (Citywide Fire Guard for Impairment)',
  'FDNY - F60 (Fire Guard for Torch Operations)',
  'FDNY - G40 (Use of LPG or Natural Gas)',
  'FDNY - G60 (Torch Use of Flammable Gas)',
  'FDNY - S56 (Construction Site Fire Safety Manager)',
  'FDNY - S92 (Supervise Portable Fueled Space Heaters)',
  'FDNY - S93 (Supervise Flammable/Combustible Liquids)',
  'NCCO - Crane Operator',
  'NCCO - Lift Director',
  'NCCO - Rigger',
  'NYC DOB - Crane Operator',
  'NYC DOB - Flagman',
  'NYC DOB - Forklift',
  'NYC DOB - Hazwoper',
  'NYC DOB - Lift Director',
  'NYC DOB - Rigger',
  'NYC DOB - Scaffold - (04 Hour)',
  'NYC DOB - Scaffold - (16 Hour Suspended)',
  'NYC DOB - Scaffold (32 Hour Supported Builder)',
  'NYC DOB - Scaffold - (32 Hour Suspended Builder)',
  'NYC DOB - Signal Person',
  'OSHA 10',
  'OSHA 30',
  'OSHA 500',
  'Other',
];

export const skipStepReasons: OnboardingStepSkipReason[] = [
  OnboardingStepSkipReason.CompletedOnPaper,
  OnboardingStepSkipReason.TechIssue,
  OnboardingStepSkipReason.TemporarilyAllowingToSkipStep,
  OnboardingStepSkipReason.ExemptFromHavingToProvideDocuments,
  OnboardingStepSkipReason.Other,
];

export const alcoholBreathTestOptions = toFancySelectOptions([
  'Pass',
  'No pass',
]);
export const medicalTestOptions = toFancySelectOptions([
  'Pass',
  'No pass',
  'Conditional pass',
]);

export const statesOptions = [
  {
    label: 'AL',
    value: StateAbbreviation.Al,
  },
  {
    label: 'AK',
    value: StateAbbreviation.Ak,
  },
  {
    label: 'AZ',
    value: StateAbbreviation.Az,
  },
  {
    label: 'AR',
    value: StateAbbreviation.Ar,
  },
  {
    label: 'CA',
    value: StateAbbreviation.Ca,
  },
  {
    label: 'CO',
    value: StateAbbreviation.Co,
  },
  {
    label: 'CT',
    value: StateAbbreviation.Ct,
  },
  {
    label: 'DE',
    value: StateAbbreviation.De,
  },
  {
    label: 'DC',
    value: StateAbbreviation.Dc,
  },
  {
    label: 'FL',
    value: StateAbbreviation.Fl,
  },
  {
    label: 'GA',
    value: StateAbbreviation.Ga,
  },
  {
    label: 'HI',
    value: StateAbbreviation.Hi,
  },
  {
    label: 'ID',
    value: StateAbbreviation.Id,
  },
  {
    label: 'IL',
    value: StateAbbreviation.Il,
  },
  {
    label: 'IN',
    value: StateAbbreviation.In,
  },
  {
    label: 'IA',
    value: StateAbbreviation.Ia,
  },
  {
    label: 'KS',
    value: StateAbbreviation.Ks,
  },
  {
    label: 'KY',
    value: StateAbbreviation.Ky,
  },
  {
    label: 'LA',
    value: StateAbbreviation.La,
  },
  {
    label: 'ME',
    value: StateAbbreviation.Me,
  },
  {
    label: 'MD',
    value: StateAbbreviation.Md,
  },
  {
    label: 'MA',
    value: StateAbbreviation.Ma,
  },
  {
    label: 'MI',
    value: StateAbbreviation.Mi,
  },
  {
    label: 'MN',
    value: StateAbbreviation.Mn,
  },
  {
    label: 'MS',
    value: StateAbbreviation.Ms,
  },
  {
    label: 'MO',
    value: StateAbbreviation.Mo,
  },
  {
    label: 'MT',
    value: StateAbbreviation.Mt,
  },
  {
    label: 'NE',
    value: StateAbbreviation.Ne,
  },
  {
    label: 'NV',
    value: StateAbbreviation.Nv,
  },
  {
    label: 'NH',
    value: StateAbbreviation.Nh,
  },
  {
    label: 'NJ',
    value: StateAbbreviation.Nj,
  },
  {
    label: 'NM',
    value: StateAbbreviation.Nm,
  },
  {
    label: 'NY',
    value: StateAbbreviation.Ny,
  },
  {
    label: 'NC',
    value: StateAbbreviation.Nc,
  },
  {
    label: 'ND',
    value: StateAbbreviation.Nd,
  },
  {
    label: 'OH',
    value: StateAbbreviation.Oh,
  },
  {
    label: 'OK',
    value: StateAbbreviation.Ok,
  },
  {
    label: 'OR',
    value: StateAbbreviation.Or,
  },
  {
    label: 'PA',
    value: StateAbbreviation.Pa,
  },
  {
    label: 'RI',
    value: StateAbbreviation.Ri,
  },
  {
    label: 'SC',
    value: StateAbbreviation.Sc,
  },
  {
    label: 'SD',
    value: StateAbbreviation.Sd,
  },
  {
    label: 'TN',
    value: StateAbbreviation.Tn,
  },
  {
    label: 'TX',
    value: StateAbbreviation.Tx,
  },
  {
    label: 'UT',
    value: StateAbbreviation.Ut,
  },
  {
    label: 'VT',
    value: StateAbbreviation.Vt,
  },
  {
    label: 'VA',
    value: StateAbbreviation.Va,
  },
  {
    label: 'WA',
    value: StateAbbreviation.Wa,
  },
  {
    label: 'WV',
    value: StateAbbreviation.Wv,
  },
  {
    label: 'WI',
    value: StateAbbreviation.Wi,
  },
  {
    label: 'WY',
    value: StateAbbreviation.Wy,
  },
];

export const timeZonesOptions: SelectOptionElement[] = tz
  .zonesForCountry('US', true)
  .map((mzo) => ({
    value: mzo.name,
    label: `${mzo.name} (UTC-${mzo.offset / 60})`,
    offset: mzo.offset,
  }))
  .sort((a, b) => a.offset - b.offset);

export const ODIN_APP_DOWNLOAD_URL = 'https://go.useodin.com/download';

export const paginationSizePerPage = 25;
export const smallTablePaginationSizePerPage = 10;

export const textFieldInputMaxLengthDefault = 150;

export const CHANGE_ASSIGNMENT_TO_NEW_JOBSITE_RADIO_TEXT =
  'Change this assignment to a new jobsite';

export const DELETE_JOBSITE_ASSIGNMENT_RADIO_TEXT =
  'Delete this jobsite assignment and all associated data';

export const CHANGE_JOBSITE_ASSIGNMENT_BANNER_TEXT = `This is a retroactive change from the beginning of 
the jobsite assignment and all historical records will be updated to reflect the new 
jobsite and contractor assignment.`;

export const DELETE_JOBSITE_ASSIGNMENT_BANNER_TEXT = `Removing this jobsite assignment without reassigning the data to 
another jobsite will result in all documents and other historical records associated with the 
original jobsite to be deleted. You cannot undo this action.`;

export const colors = {
  odinBlack: '#001024',
  odinNavy: '#1B2C41',
  odinDarkGray: '#617A98',
  odinMediumGray: '#EAEFF4',
  odinOffWhite: '#F9FBFD',
  odinWhite: '#FFFFFF',
  odinPrimary: '#5F1F8A',
  odinGrape500: '#A370E6',
  odinGrape300: '#D6C9F5',
  odinGrape100: '#F0F0FF',
  odinDarker: 'rgba(0, 16, 36, 0.24)',
  odinHighlight: '#F0F0FF',
  odinWarning: '#F5C23E',
  odinLightYellow: '#FFFBED',
  odinDanger: '#E63757',
  odinLightRed: '#FFEEEE',
  odinSuccess: '#00C673',
  odinLightGreen: '#EBFAF4',
  odinNeon: '#CBFFF3',
  odinTwoTone: '#4C1272',
  'odinTableGradient-1': '#E4EEFA',
  'odinTableGradient-2': '#F3F9FF',
  odinEnvLocal: '#E63757',
  odinEnvDev: '#F55F3E',
  odinEnvQa: '#F5C23E',
  odinEnvStg: '#00C673',
};
